import { useTranslation } from 'react-i18next';

import { FutureTimeframeDays, LastCompletedVisitFilterOperator, PastTimeframeDays, VisitFilterOperator } from './_types';
import {
  COMPLETED_VISIT_TIMEFRAME_OPTIONS,
  LAST_COMPLETED_VISIT_OPERATORS,
  STANDARD_PAST_TIMEFRAME_OPTIONS,
  UPCOMING_VISIT_TIMEFRAME_OPTIONS,
  VISIT_FILTER_OPERATORS,
  VisitFilterSelectOption,
} from './constants';
import { VisitFilterValidationErrorKey } from './visitFilterValidation';

const mapOptions = <T extends string | number>(
  options: VisitFilterSelectOption<T>[],
  labelFor: (value: T) => string
): VisitFilterSelectOption<T>[] =>
  options.map((option) => ({
    ...option,
    label: labelFor(option.value),
  }));

export const useClientVisitFilterLabels = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'clients.page.visitFilters',
  });

  const operators = mapOptions(VISIT_FILTER_OPERATORS, (value) =>
    t(`operators.${value as VisitFilterOperator}`)
  );

  const lastCompletedOperators = mapOptions(
    LAST_COMPLETED_VISIT_OPERATORS,
    (value) => t(`operators.${value as LastCompletedVisitFilterOperator}`)
  );
  const lastCompletedTimeframes = mapOptions(STANDARD_PAST_TIMEFRAME_OPTIONS, (value) =>
    t(`timeframes.lastCompleted.${value as PastTimeframeDays}`)
  );

  const completedTimeframes = mapOptions(
    COMPLETED_VISIT_TIMEFRAME_OPTIONS,
    (value) => t(`timeframes.past.${value as PastTimeframeDays}`)
  );

  const pastTimeframes = mapOptions(STANDARD_PAST_TIMEFRAME_OPTIONS, (value) =>
    t(`timeframes.past.${value as PastTimeframeDays}`)
  );

  const upcomingTimeframes = mapOptions(
    UPCOMING_VISIT_TIMEFRAME_OPTIONS,
    (value) => t(`timeframes.future.${value as FutureTimeframeDays}`)
  );

  const getValidationError = (key: VisitFilterValidationErrorKey) =>
    t(`validation.${key}`);

  return {
    comparisonLabel: t('label.operator.default'),
    visitCountLabel: t('label.visitCount.default'),
    timeframeLabel: t('label.timeframe'),
    selectPlaceholder: t('selectPlaceholder'),
    getValidationError,
    operators,
    lastCompletedOperators,
    lastCompletedTimeframes,
    completedTimeframes,
    pastTimeframes,
    upcomingTimeframes,
  };
};
